import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { sanitizeDbError } from '@/lib/sanitizeError';
import {
  JobValidationError,
  getJobPublishErrorTranslationKey,
  normalizeJobValidationError,
  validateJobForPublish,
} from '@/lib/jobPublishValidation';
import type { Json, Tables } from '@/integrations/supabase/types';
import {
  validateJobCommandResult,
  validatePersistedJobReadBack,
} from '@/lib/forkhire/jobCommandResult';

export type Job = Tables<'jobs'>;

type CreateJobInput = {
  title: string;
  department?: string;
  location?: string;
  employment_type?: string;
  description?: string;
  requirements_json?: Json;
  cv_pass_threshold?: number;
  interview_pass_threshold?: number;
  status?: string;
  job_type?: string;
  seniority?: string;
  min_experience_years?: number;
  education_level?: string;
  certifications?: string[];
  languages?: string[];
  salary_min?: number | null;
  salary_max?: number | null;
  salary_currency?: string;
  notice_period?: string | null;
  start_date?: string | null;
  skills?: string[];
  open_duration_days?: number;
  visibility_mode?: string;
  number_of_openings?: number;
  existingDraftJobId?: string | null;
  publishIdempotencyKey: string;
  onDatabaseDraftCreated?: (draftJobId: string) => void;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function toJson(value: unknown): Json {
  if (value === null) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.map(toJson);
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).filter(([, item]) => item !== undefined).map(([key, item]) => [key, toJson(item)]),
    );
  }
  return String(value);
}

export function useJobs(options?: { refetchOnWindowFocus?: boolean }) {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery({
    queryKey: ['jobs', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as Job[];
    },
    enabled: !!orgId,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
  });
}

export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: ['jobs', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', id!)
        .single();
      if (error) throw error;
      return data as Job;
    },
    enabled: !!id,
  });
}

export function useJobApplicationCounts(jobId: string | undefined) {
  return useQuery({
    queryKey: ['job-app-counts', jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('applications')
        .select('stage')
        .eq('job_id', jobId!);
      if (error) throw error;
      const counts = { total: data.length, applied: 0, prescreening: 0, shortlisted: 0, interviewing: 0, interview_scoring: 0, acceptance: 0, second_interview: 0, offer: 0, offer_accepted: 0, hired: 0, rejected: 0 };
      data.forEach((a) => {
        const stage = a.stage as keyof typeof counts;
        if (stage in counts) counts[stage]++;
      });
      return counts;
    },
    enabled: !!jobId,
  });
}

export function useCreateJob() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (input: CreateJobInput) => {
      if (!profile?.org_id) throw new Error('No organization');
      const { existingDraftJobId, publishIdempotencyKey, onDatabaseDraftCreated, ...jobInput } = input;
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const payload = {
        ...jobInput,
        default_assignee: userId,
      };
      const publish = input.status === 'active';
      if (publish) {
        const fields = validateJobForPublish({ ...payload, public_slug: 'pending-slug' });
        if (fields.length > 0) throw new JobValidationError(fields);
      }

      const { data, error } = await supabase.rpc('save_job_draft_or_publish', {
        _org_id: profile.org_id,
        _job_input: toJson(payload),
        _publish: publish,
        _idempotency_key: publishIdempotencyKey,
        _draft_job_id: existingDraftJobId ?? null,
      });
      const result = asRecord(data);
      const retainedDraftId = typeof result.draft_id === 'string' ? result.draft_id : existingDraftJobId ?? null;
      if (error) throw { ...error, draftId: retainedDraftId };
      if (result.success === false) throw { ...asRecord(result.error || result), draftId: retainedDraftId };
      const commandResult = validateJobCommandResult<Job>({
        data: result,
        orgId: profile.org_id,
        title: payload.title,
        publish,
        commandKey: publishIdempotencyKey,
      });
      const { data: persistedJob, error: readBackError } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', commandResult.draftId)
        .eq('org_id', profile.org_id)
        .maybeSingle();
      if (readBackError) {
        throw { ...readBackError, draftId: commandResult.draftId };
      }
      const verifiedJob = validatePersistedJobReadBack<Job>({
        job: persistedJob,
        rpcJob: commandResult.job,
        draftId: commandResult.draftId,
        orgId: profile.org_id,
        title: payload.title,
        publish,
        commandKey: publishIdempotencyKey,
      });
      onDatabaseDraftCreated?.(commandResult.draftId);
      return verifiedJob;
    },
    onSuccess: async () => {
      toast({ title: 'Job created successfully' });
    },
    onError: (e) => {
      const validation = normalizeJobValidationError(e);
      toast({
        title: validation ? t('hire.createJob.cannotPublishJob') : t('hire.createJob.publishErrors.createFailedTitle'),
        description: validation
          ? t(getJobPublishErrorTranslationKey(validation.code))
          : sanitizeDbError(e),
        variant: 'destructive',
      });
    },
    onSettled: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['jobs'], refetchType: 'all' }),
        qc.invalidateQueries({ queryKey: ['dashboard'], refetchType: 'all' }),
        qc.invalidateQueries({ queryKey: ['entitlement'], refetchType: 'all' }),
      ]);
    },
  });
}

export function useUpdateJob() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Job> & { id: string }) => {
      if (updates.status === 'active') {
        const { data, error } = await supabase.rpc('activate_job', {
          _job_id: id,
          _updates: toJson(updates),
        });
        if (error) throw error;
        const result = asRecord(data);
        if (result.success === false) throw result.error || result;
        return result.job as Job;
      }

      const { data, error } = await supabase
        .from('jobs')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({ title: 'Job updated' });
    },
    onError: (e) => {
      const validation = normalizeJobValidationError(e);
      toast({
        title: validation ? t('hire.createJob.cannotPublishJob') : t('hire.createJob.publishErrors.updateFailedTitle'),
        description: validation
          ? t(getJobPublishErrorTranslationKey(validation.code))
          : sanitizeDbError(e),
        variant: 'destructive',
      });
    },
    onSettled: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['jobs'], refetchType: 'all' }),
        qc.invalidateQueries({ queryKey: ['dashboard'], refetchType: 'all' }),
        qc.invalidateQueries({ queryKey: ['entitlement'], refetchType: 'all' }),
      ]);
    },
  });
}
